// insertScaffolds — the WYSIWYG insert palette's construct catalogue (plan
// 2026-07-23-process-wysiwyg-editor.md §4b). Each chip inserts a validated
// v0.10 process-step scaffold (brace-form control blocks, owner/callee as
// draft-floor stubs `A`/`B` the author fills in). Multi-line scaffolds rely on
// the engine's indent-normalizing insert (`indentScaffold`) to nest correctly.
//
// Pure data + a tiny helper — no component, no engine import — so it is shared
// by the swimlane / BPMN / tree insert affordances and unit-tested for parse
// validity (insertScaffolds.test.ts) independent of any UI.

/** One palette entry: a short chip label + the exact text inserted. */
export interface InsertScaffold {
  /** Stable key (also the construct keyword, for slash-typed `/if` matching). */
  readonly key: string;
  /** Chip label shown in the palette. */
  readonly label: string;
  /** The scaffold text inserted (relative indent from column 0). */
  readonly text: string;
}

/** The insert palette, in display order. Control blocks are brace-form with an
 *  owner prefix; `await` takes a quoted head; an inbound event is a plain step;
 *  `catch` takes a bare error type — all validated against PROCESS.md/examples. */
export const INSERT_SCAFFOLDS: readonly InsertScaffold[] = [
  { key: "call", label: "call", text: "A > B.action" },
  { key: "if", label: "if", text: 'A if "condition" {\n    A > B.action\n}' },
  { key: "select", label: "select", text: "A select one Type {\n    caseA: A > B.action\n}" },
  { key: "parallel", label: "parallel", text: "A parallel {\n    A > B.action\n}" },
  { key: "each", label: "each", text: "A each item in items {\n    A > B.action\n}" },
  { key: "try", label: "try", text: "A try {\n    A > B.action\n} catch SomeError {\n    A > B.recover\n}" },
  { key: "reversible", label: "reversible", text: "A reversible {\n    A > B.action  unwind A > B.undo\n}" },
  { key: "await", label: "await", text: 'A await "1 day"' },
  { key: "event", label: "event", text: "Ext > Us.event" },
  { key: "finish", label: "finish", text: 'finish "done"' },
  { key: "fail", label: "fail", text: 'fail "reason"' },
  { key: "do", label: "do", text: "do Subprocess" },
] as const;

/** A `select` body holds ONLY cases (`label: step` / `label { … }`), never bare
 *  steps — so its insert palette is case-shaped. Offering the normal step palette
 *  here would author a parse error (guarded at the funnel by `guardInsertParse`,
 *  but the palette should never tempt it). */
export const INSERT_SCAFFOLDS_CASE: readonly InsertScaffold[] = [
  { key: "case", label: "case", text: "caseName: A > B.action" },
  { key: "caseblock", label: "case { }", text: "caseName {\n    A > B.action\n}" },
] as const;

/** Which construct directly encloses the insertion point. `select` bodies take
 *  cases; every other body (flow / `if` arm / `parallel` / `each` / `try` /
 *  `reversible`) takes steps. */
export type InsertEnclosing = "select" | "flow";

/** The legal palette for an insertion point — the contextual-valid filter
 *  (plan §127): a `select` context offers cases, everything else offers steps. */
export function scaffoldsFor(enclosing: InsertEnclosing): readonly InsertScaffold[] {
  return enclosing === "select" ? INSERT_SCAFFOLDS_CASE : INSERT_SCAFFOLDS;
}

/** Match a slash-typed query (`/if`, `if`, `par…`) to the legal palette entries
 *  for `enclosing` by key prefix — the Notion-style quick filter in the typed
 *  input. Empty query → the whole legal palette. */
export function matchScaffolds(query: string, enclosing: InsertEnclosing = "flow"): readonly InsertScaffold[] {
  const q = query.replace(/^\//, "").trim().toLowerCase();
  const catalogue = scaffoldsFor(enclosing);
  if (q === "") return catalogue;
  return catalogue.filter((s) => s.key.startsWith(q));
}
